import React from 'react';
import '../../styles/v4-theme.css';
import '../../styles/precision-enhancements.css';

/**
 * TargetCard Component
 * Displays a summary card for a single V4 target
 */
const TargetCard = ({ target, onClick, selected = false, onTimestampClick }) => {
  // DEBUG: Log what we're receiving
  console.log('🎴 TargetCard received target:', {
    id: target.id,
    type: target.type,
    hasAttributes: !!target.attributes,
    hasSpatial: !!target.spatial,
    hasActions: !!target.actions,
    fullTarget: target
  });

  // Handle timestamp click (stop propagation to avoid opening modal)
  const handleTimestampClick = (e, timestamp) => {
    e.stopPropagation();
    if (onTimestampClick) {
      onTimestampClick(timestamp, target);
    }
  };

  // Helper: Get target icon (professional symbols)
  const getTargetIcon = (target) => {
    const targetType = target?.type || target?.target_type;
    const icons = {
      vehicle: '◆',
      person: '●',
      environment: '▲',
      videographer: '■',
      text: '⊞',
      weapon: '▼',
      object: '◇',
      unknown: '○'
    };
    return icons[targetType] || icons.unknown;
  };

  // Helper: Get target title
  const getTargetTitle = (target) => {
    const targetType = target.type || target.target_type;
    
    if (targetType === 'vehicle') {
      const attrs = target.attributes || {};
      const make = attrs.make || '';
      const model = attrs.model || '';
      const year = attrs.year || '';
      const color = attrs.color || '';
      
      // Build title with available parts
      let title = '';
      if (color && color !== 'unknown') title += color + ' ';
      if (year && year !== 'unknown') title += year + ' ';
      if (make && make !== 'unknown') title += make + ' ';
      if (model && model !== 'unknown') title += model;
      
      // Clean up and return
      title = title.trim();
      if (title) return title;
      
      // Fallback
      if (color) return `${color} Vehicle`;
      return 'Vehicle';
    }
    
    if (targetType === 'environment') {
      const location = target.attributes?.location_type || '';
      if (location && location !== 'unknown') return location;
      return 'Environment';
    }
    
    if (targetType === 'videographer') {
      const device = target.attributes?.device_type || target.attributes?.camera_type || '';
      if (device && device !== 'unknown') return device;
      return 'Videographer';
    }

    return targetType ? targetType.charAt(0).toUpperCase() + targetType.slice(1) : 'Unknown';
  };

  // Helper: Get target subtitle
  const getTargetSubtitle = (target) => {
    const targetType = target.type || target.target_type;
    
    if (targetType === 'vehicle') {
      return target.attributes?.vehicle_type || target.attributes?.type || 'Vehicle';
    }
    return targetType || 'Unknown';
  };

  // Helper: Get confidence level class
  const getConfidenceLevel = (confidence) => {
    if (confidence >= 0.9) return 'high';
    if (confidence >= 0.7) return 'medium';
    return 'low';
  };

  // Helper: Get verification badge for precision scans
  const getVerificationBadge = (target) => {
    // Only show for precision-enhanced targets
    if (!target.precision_enhanced) return null;

    const anchorType = target.anchor_type;
    
    if (anchorType === 'PRIMARY') {
      return (
        <div className="verification-badge primary">
          <span className="verification-badge-icon">🔐</span>
          <span>VI VERIFIED (PRIMARY)</span>
        </div>
      );
    } else if (anchorType === 'SECONDARY') {
      return (
        <div className="verification-badge secondary">
          <span className="verification-badge-icon">📍</span>
          <span>VI VERIFIED (SECONDARY)</span>
        </div>
      );
    } else if (anchorType === 'TERTIARY') {
      return (
        <div className="verification-badge tertiary">
          <span className="verification-badge-icon">🏷️</span>
          <span>VI VERIFIED (LABEL)</span>
        </div>
      );
    } else if (anchorType === 'NONE' || target.vi_verified === false) {
      return (
        <div className="verification-badge none">
          <span className="verification-badge-icon">⚠️</span>
          <span>UNVERIFIED</span>
        </div>
      );
    }

    return null;
  };

  // Helper: Get top 3 notable features
  const getTopFeatures = (target) => {
    const features = [];
    const targetType = target.type || target.target_type;

    if (targetType === 'vehicle') {
      const attrs = target.attributes || {};
      
      // Priority 1: Year (if available)
      if (attrs.year && attrs.year !== 'unknown') {
        features.push(`Year: ${attrs.year}`);
      }
      
      // Priority 2: License plate
      if (attrs.license_plate && attrs.license_plate !== 'not visible' && attrs.license_plate !== 'unknown') {
        features.push(`Plate: ${attrs.license_plate}`);
      }
      
      // Priority 3: Damage (if present)
      if (attrs.damage && attrs.damage !== 'none' && attrs.damage !== 'No' && attrs.damage !== 'not visible' && attrs.damage !== 'unknown') {
        features.push(`Damage: ${attrs.damage}`);
      }
      
      // Priority 4: Notable features
      if (attrs.notable_features && attrs.notable_features !== 'unknown') {
        const feat = attrs.notable_features.split('.')[0].substring(0, 50);
        features.push(feat);
      }
    } else if (targetType === 'environment') {
      const attrs = target.attributes || {};
      if (attrs.weather) features.push(`Weather: ${attrs.weather}`);
      if (attrs.lighting) features.push(`Lighting: ${attrs.lighting}`);
      if (attrs.location_type) features.push(`Type: ${attrs.location_type}`);
    } else if (targetType === 'videographer') {
      const attrs = target.attributes || {};
      if (attrs.camera_type) features.push(`Camera: ${attrs.camera_type}`);
      if (attrs.stability) features.push(`Stability: ${attrs.stability}`);
      if (attrs.quality) features.push(`Quality: ${attrs.quality}`);
    }

    // Fallback: try to extract from attributes object
    if (features.length === 0 && target.attributes) {
      const attrs = target.attributes;
      Object.keys(attrs).slice(0, 3).forEach(key => {
        const value = attrs[key];
        if (value && typeof value === 'string' && value !== 'unknown' && value !== 'not visible') {
          features.push(`${key}: ${value}`.substring(0, 40));
        }
      });
    }

    return features.slice(0, 3);
  };

  const title = getTargetTitle(target);
  const subtitle = getTargetSubtitle(target);
  const confidenceLevel = getConfidenceLevel(target.confidence || 0);
  const features = getTopFeatures(target);
  const activitiesCount = target.actions?.activities?.length || 0;
  const relationshipsCount = target.actions?.relationships?.length || 0;
  const verificationBadge = getVerificationBadge(target);

  // Session 7 Hybrid Model: Multi-appearance and plate detection data
  const appearanceSegments = target.appearance_segments || [];
  const appearanceCount = appearanceSegments.length || target.appearance_count || 1;
  const legalStats = target.legal_report_stats || {};
  const totalPlateDetections = legalStats.total_plate_detections || 0;
  const verifiedWindows = legalStats.verified_windows || 0;

  // Format time range - Use precision timestamps if available
  const isPrecisionEnhanced = target.precision_enhanced === true;
  const firstAppearance = isPrecisionEnhanced && target.first_seen 
    ? target.first_seen 
    : (target.spatial?.first_appearance || '00:00:00');
  const lastAppearance = isPrecisionEnhanced && target.last_seen
    ? target.last_seen
    : (target.spatial?.last_appearance || '00:00:00');
  const timeRange = `${firstAppearance}-${lastAppearance}`;
  const duration = target.duration_seconds 
    ? `${Math.round(target.duration_seconds)}s`
    : (target.spatial?.total_visible_duration || 'Unknown');

  const targetType = target.type || target.target_type;

  return (
    <div
      className={`v4-target-card ${selected ? 'selected' : ''}`}
      onClick={() => onClick(target)}
    >
      {/* Header */}
      <div className="v4-card-header">
        <div className={`v4-card-icon type-${targetType || 'unknown'}`}>
          {getTargetIcon(target)}
        </div>
        <div className="v4-card-title-group">
          <div className="v4-card-title" title={title}>
            {title}
          </div>
          <div className="v4-card-subtitle" title={subtitle}>
            {subtitle}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="v4-card-body">
        {/* Verification Badge - Only for precision scans */}
        {verificationBadge}

        {/* Confidence Badge */}
        <div className={`v4-confidence-badge ${confidenceLevel}`}>
          <span className="v4-confidence-dot"></span>
          {Math.round((target.confidence || 0) * 100)}% confidence
        </div>

        {/* Time Range */}
        <div className="v4-card-time-range">
          <span className="v4-time-icon">▸</span>
          {onTimestampClick ? (
            <span 
              className="v4-timestamp-link"
              onClick={(e) => handleTimestampClick(e, firstAppearance)}
              title="Click to jump to video"
            >
              {timeRange}
            </span>
          ) : (
            <span>{timeRange}</span>
          )}
        </div>
        <div className="v4-card-time-range">
          <span className="v4-time-icon">◷</span>
          <span>{duration}</span>
        </div>

        {/* Stats - Compact horizontal layout */}
        <div className="v4-card-stats-compact">
          {/* Show appearance count for multi-appearance targets */}
          {appearanceCount > 1 && (
            <>
              <div className="v4-stat-item" title="Number of separate appearance windows">
                <span className="v4-stat-value">{appearanceCount}</span>
                <span className="v4-stat-label">appearances</span>
              </div>
              <div className="v4-stat-divider">|</div>
            </>
          )}
          {/* Show plate detection count if available (Session 7 hybrid model) */}
          {totalPlateDetections > 0 ? (
            <>
              <div className="v4-stat-item" title="Times plate was verified by OCR">
                <span className="v4-stat-value" style={{ color: 'var(--success)' }}>{totalPlateDetections}</span>
                <span className="v4-stat-label">plate reads</span>
              </div>
              <div className="v4-stat-divider">|</div>
            </>
          ) : null}
          <div className="v4-stat-item">
            <span className="v4-stat-value">{activitiesCount}</span>
            <span className="v4-stat-label">activities</span>
          </div>
          <div className="v4-stat-divider">|</div>
          <div className="v4-stat-item" title="Connections to other targets (e.g., 'Person exited this vehicle')">
            <span className="v4-stat-value">{relationshipsCount}</span>
            <span className="v4-stat-label">related</span>
          </div>
        </div>

        {/* Key Features */}
        {features.length > 0 && (
          <div className="v4-card-features">
            <div className="v4-card-feature-label">Key Features:</div>
            <ul className="v4-card-feature-list">
              {features.map((feature, idx) => (
                <li key={idx} className="v4-card-feature-item">
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="v4-card-footer">
        <button className="v4-card-button">
          View Details →
        </button>
      </div>
    </div>
  );
};

export default TargetCard;

